import React, {useEffect, useState} from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import Canvas from "./dahboard_canvas";
import DashboardFooter from "./dashboard_footer";
import EHRNav from "./components/EHRNav";
import axios from "axios";
import Swal from "sweetalert2";
import localforage from "localforage";
import {useBaseUrl} from "../../utils/utils";
import { Link } from "react-router-dom";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface SharedRecord {
    id: string;
    patient_name: string;
    medical_record_id: string;
    medical_record_title: string;
    shared_by: string;
    shared_by_clinic: string;
    consent_status: string;
    start_date: string;
    end_date: string | null;
    purpose: string;
    scope: string;
    created_at: string;
}

const SharedRecords: React.FC = () =>{
    const [step, setStep] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sharedRecords, setSharedRecords] = useState<SharedRecord[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<SharedRecord | null>(null);
    const [filter, setFilter] = useState<string>("all");
    const [searchTerm, setSearchTerm] = useState<string>("");

    const base_url = useBaseUrl();

    useEffect(()=>{
        document.title = "Received Medical Records";
        loadSharedRecords();
    }, [])

    // Function to load shared records
    const loadSharedRecords = async () => {
        setIsLoading(true);

        try {
            const clinic_id = await localforage.getItem("clinic_id");

            // Use the new RESTful API endpoint
            // Filter consents where this clinic is the recipient (consented_to)
            const url = `${base_url}ehr/api/v1/patient-consents/?consented_to=${clinic_id}`;

            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    // Add any authentication headers if needed
                }
            }).finally(() => {
                setIsLoading(false);
            });

            const { status, message, data } = response.data;

            if (status === "success") {
                // Transform the data to match the expected format if needed
                const formattedData = data.map((consent: any) => ({
                    id: consent.id,
                    patient_name: consent.patient.name || `${consent.patient.first_name} ${consent.patient.last_name}`,
                    medical_record_id: consent.medical_record.id,
                    medical_record_title: consent.medical_record.name,
                    shared_by: consent.created_by ? `${consent.created_by.first_name} ${consent.created_by.last_name}` : 'Unknown',
                    shared_by_clinic: consent.medical_record.clinic.name,
                    consent_status: consent.status,
                    start_date: consent.start_date,
                    end_date: consent.end_date,
                    purpose: consent.purpose,
                    scope: consent.scope,
                    created_at: consent.created_at
                }));

                setSharedRecords(formattedData);

                if (formattedData.length === 0) {
                    await Swal.fire({
                        icon: "info",
                        title: "No Shared Records",
                        text: "No records have been shared with your clinic."
                    });
                }
            } else {
                await Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: message || "Failed to load shared records"
                });
            }
        } catch (error: any) {
            setIsLoading(false);

            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "Failed to load shared records. Please check your connection and try again."
            });
        }
    }

    // Function to view a shared record
    const viewRecord = (record: SharedRecord) => {
        setSelectedRecord(record);
        setStep(2);
    }

    // Function to filter records
    const filterRecords = () => {
        if (filter === "all") {
            return sharedRecords.filter(record => 
                record.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.shared_by_clinic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.medical_record_title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        } else {
            return sharedRecords.filter(record => 
                record.consent_status === filter &&
                (record.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.shared_by_clinic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.medical_record_title.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
    }
    const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    const STATUS_MAP: Record<string, { bg: string; color: string; label: string }> = {
        active: { bg: ACCENT_SOFT, color: ACCENT, label: 'Active' },
        pending: { bg: 'rgba(245,158,11,0.1)', color: '#d97706', label: 'Pending' },
        rejected: { bg: 'rgba(244,63,94,0.1)', color: '#e11d48', label: 'Rejected' },
        expired: { bg: 'rgba(100,116,139,0.1)', color: '#64748b', label: 'Expired' },
        inactive: { bg: 'rgba(15,23,42,0.06)', color: '#475569', label: 'Inactive' },
    };

    const statusBadge = (s: string) => {
        const c = STATUS_MAP[s] || { bg: 'rgba(100,116,139,0.1)', color: '#64748b', label: s };
        return <span style={{ padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: '0.68rem', fontWeight: 700 }}>{c.label}</span>;
    };

    const filtered = filterRecords();

    const stats = {
        total: sharedRecords.length,
        active: sharedRecords.filter(r => r.consent_status === 'active').length,
        pending: sharedRecords.filter(r => r.consent_status === 'pending').length,
        rejected: sharedRecords.filter(r => r.consent_status === 'rejected').length,
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-cloud-download me-2" style={{ color: ACCENT }}></i>Shared With Us</h1>
                                <p>Medical records shared with your facility by other clinics</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadSharedRecords} disabled={isLoading}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/ehr/share" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-export"></i> Share Records</Link>
                            </div>
                        </div>

                        <EHRNav />

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Shared', val: stats.total, icon: 'bx bx-cloud-download', cls: 'teal' },
                                { label: 'Active', val: stats.active, icon: 'bx bx-check-circle', cls: 'blue' },
                                { label: 'Pending', val: stats.pending, icon: 'bx bx-time', cls: 'amber' },
                                { label: 'Rejected', val: stats.rejected, icon: 'bx bx-x-circle', cls: 'rose' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* List View */}
                        {step === 1 && (
                            <>
                                {/* Filters */}
                                <div className="sc-card" style={{ marginBottom: 16 }}>
                                    <div className="sc-card-body" style={{ padding: '14px 20px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div className="sc-search" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
                                            <i className="bx bx-search"></i>
                                            <input type="text" placeholder="Search patient, clinic, or record title..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '8px 14px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)', minWidth: 140 }}>
                                            <option value="all">All Statuses</option>
                                            <option value="active">Active</option>
                                            <option value="pending">Pending</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="rejected">Rejected</option>
                                            <option value="expired">Expired</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Content */}
                                {isLoading ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="sc-card" style={{ height: 64, opacity: 0.5 }}>
                                                <div className="sc-card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px' }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e2e8f0' }}></div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ width: '35%', height: 12, borderRadius: 6, background: '#e2e8f0', marginBottom: 6 }}></div>
                                                        <div style={{ width: '55%', height: 10, borderRadius: 6, background: '#e2e8f0' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : filtered.length === 0 ? (
                                    <div className="sc-card">
                                        <div className="sc-card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
                                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                                <i className="bx bx-cloud-download" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                            </div>
                                            <p style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 6 }}>No Shared Records</p>
                                            <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                {searchTerm || filter !== 'all' ? 'No records match your current filters.' : 'No records have been shared with your clinic yet.'}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="sc-card" style={{ overflow: 'hidden' }}>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                <thead>
                                                    <tr style={{ background: 'rgba(15,23,42,0.025)' }}>
                                                        {['Patient', 'Record Title', 'Shared By', 'Status', 'Date', ''].map((h, i) => (
                                                            <th key={i} style={{ padding: '10px 14px', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', borderBottom: '1px solid var(--uzima-shell-border)' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map((r, i) => (
                                                        <tr key={i} style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                                                            onClick={() => viewRecord(r)}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(15,118,110,0.03)'; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                                                        >
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.65rem', flexShrink: 0 }}>
                                                                        {r.patient_name.split(' ').map(n => n.charAt(0)).join('').slice(0, 2)}
                                                                    </div>
                                                                    <span style={{ fontWeight: 600 }}>{r.patient_name}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)', fontWeight: 600 }}>{r.medical_record_title}</td>
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>{r.shared_by_clinic}</td>
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>{statusBadge(r.consent_status)}</td>
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                                                            <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                                <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={e => { e.stopPropagation(); viewRecord(r); }}><i className="bx bx-show"></i></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Detail View */}
                        {step === 2 && selectedRecord && (
                            <>
                                <button className="sc-btn sc-btn-ghost" onClick={() => setStep(1)} style={{ marginBottom: 16 }}><i className="bx bx-arrow-back"></i> Back to List</button>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                    {/* Record Info */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-file me-2" style={{ color: ACCENT }}></i>Record Information</div>
                                        </div>
                                        <div className="sc-card-body">
                                            {[
                                                { label: 'Patient Name', value: selectedRecord.patient_name },
                                                { label: 'Record Title', value: selectedRecord.medical_record_title },
                                                { label: 'Record ID', value: selectedRecord.medical_record_id },
                                            ].map((item, i) => (
                                                <div key={i} style={{ marginBottom: 12 }}>
                                                    <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 2 }}>{item.label}</div>
                                                    <div style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--uzima-shell-text)' }}>{item.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Sharing Info */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: '2px solid #3b82f6', background: 'rgba(59,130,246,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-share-alt me-2" style={{ color: '#3b82f6' }}></i>Sharing Information</div>
                                        </div>
                                        <div className="sc-card-body">
                                            {[
                                                { label: 'Shared By', value: selectedRecord.shared_by },
                                                { label: 'Clinic', value: selectedRecord.shared_by_clinic },
                                                { label: 'Shared Date', value: fmtDate(selectedRecord.created_at) },
                                            ].map((item, i) => (
                                                <div key={i} style={{ marginBottom: 12 }}>
                                                    <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 2 }}>{item.label}</div>
                                                    <div style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--uzima-shell-text)' }}>{item.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Consent Details */}
                                <div className="sc-card">
                                    <div style={{ padding: '12px 18px', borderBottom: '2px solid #8b5cf6', background: 'rgba(139,92,246,0.03)' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-shield-quarter me-2" style={{ color: '#8b5cf6' }}></i>Consent Details</div>
                                    </div>
                                    <div className="sc-card-body">
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                                            <div>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>Status</div>
                                                {statusBadge(selectedRecord.consent_status)}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>Start Date</div>
                                                <div style={{ fontSize: '0.86rem', fontWeight: 600 }}>{fmtDate(selectedRecord.start_date)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>End Date</div>
                                                <div style={{ fontSize: '0.86rem', fontWeight: 600 }}>{selectedRecord.end_date ? fmtDate(selectedRecord.end_date) : 'No end date'}</div>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: 12 }}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>Purpose</div>
                                            <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text)', lineHeight: 1.6 }}>{selectedRecord.purpose}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}>Scope</div>
                                            <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text)', lineHeight: 1.6 }}>{selectedRecord.scope}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 16 }}>
                                    <Link to={`/institution/ehr?patient_id=${selectedRecord.patient_name}`} className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}>
                                        <i className="bx bx-file"></i> View Full Record
                                    </Link>
                                    <button className="sc-btn sc-btn-ghost" onClick={() => setStep(1)}><i className="bx bx-arrow-back"></i> Back to List</button>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    )
}

export default SharedRecords;
